import React, { useEffect, useState } from "react";
import { fetchPromiseDryRun } from "../services/analyticsService";
import AnalyticsSection from "./AnalyticsSection";
import PanelHeadline, { Fig, Delta } from "./PanelHeadline";

// The Curiouzz Promise (founder "add the panel", 2026-09-01; "updated it",
// 2026-09-05). The server runs the ladder's own scanner live and says, per
// pair, what the 6:00 PM tick WILL do and when. Nothing on this panel acts;
// the tick does, so the wording is "will" when the live ladder is on and
// "would" when it is dormant. Day numbers and dates come from the sweep
// (shape-aware since 09-04: a ghosted Vault runs on a 24h / 48h clock),
// never hardcoded - the badges went stale once already.
const fmtWhen = (iso, nextTickIso) => {
  if (!iso) return "";
  const d = new Date(iso);
  // "tonight" only when the tick is actually today; after 6 PM the next
  // tick is tomorrow and used to be labelled tonight (founder 2026-09-07).
  const dayOf = (x) => x.toLocaleDateString("en-CA", { timeZone: "America/Toronto" });
  const today = dayOf(new Date());
  const tomorrow = dayOf(new Date(Date.now() + 24 * 60 * 60 * 1000));
  if (dayOf(d) === today) return "tonight 6:00 PM";
  if (dayOf(d) === tomorrow) return "tomorrow 6:00 PM";
  return d.toLocaleDateString("en-CA", { month: "short", day: "numeric", timeZone: "America/Toronto" }) + " 6:00 PM";
};

const planLabel = (c, live, nextTickIso) => {
  const verb = live ? "Will" : "Would";
  const p = c.plan;
  if (!p) return c.bucket;
  switch (p.action) {
    case "prompt":
      return `${verb} prompt ${fmtWhen(p.actionAt, nextTickIso)}`;
    case "prompt_then_close":
      return `${verb} prompt ${fmtWhen(p.actionAt, nextTickIso)}, close ${fmtWhen(p.closeAt, nextTickIso)}`;
    case "close":
      return `${verb} close ${fmtWhen(p.actionAt, nextTickIso)}`;
    case "mutual_close":
      return `${verb} close both ${fmtWhen(p.actionAt, nextTickIso)}`;
    case "watch":
      return `Watching · both-quiet close ${fmtWhen(p.actionAt, nextTickIso)}`;
    default:
      return c.bucket === "two_way_stalled" ? "Two-way, stalled (never laddered)" : c.bucket;
  }
};

const planClass = (c) => {
  switch (c.plan?.action) {
    case "close":
    case "mutual_close":
      return "bg-danger";
    case "prompt":
    case "prompt_then_close":
      return "bg-warning text-dark";
    default:
      return "bg-secondary";
  }
};

const shapeLabel = (c) =>
  c.shape === "ghosted_vault"
    ? "Ghosted Vault (waiter finished, partner at 0)"
    : c.stage === "chat"
      ? "Revealed, one side quiet"
      : "Vault, one side quiet";

const CuriouzzPromiseStats = ({ startLoading, stopLoading }) => {
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);
  const [showQuiet, setShowQuiet] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        startLoading();
        setFailed(false);
        const res = await fetchPromiseDryRun();
        setData(res?.data?.data || null);
      } catch (err) {
        console.error("Failed to load the Promise plan", err);
        setFailed(true);
      } finally {
        stopLoading();
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const live = data?.liveFlag === true;
  const report = data?.report;
  const summary = report?.summary;
  const nextTickIso = report?.nextTickAt;
  const candidates = report?.candidates || [];
  const actionable = candidates.filter(
    (c) => c.bucket === "would_close" || c.bucket === "would_prompt"
  );
  const quiet = candidates.filter(
    (c) => c.bucket === "both_quiet" || c.bucket === "two_way_stalled"
  );
  const tonight = (action) =>
    candidates.filter(
      (c) =>
        c.plan &&
        nextTickIso &&
        Math.abs(new Date(c.plan.actionAt) - new Date(nextTickIso)) < 60 * 1000 &&
        (Array.isArray(action) ? action.includes(c.plan.action) : c.plan.action === action)
    ).length;
  const prompts = tonight(["prompt", "prompt_then_close"]);
  const closes = tonight(["close", "mutual_close"]);

  return (
    <AnalyticsSection
      id="promise"
      icon="mdi:clock-outline"
      title={`The Curiouzz Promise: ${live ? "tonight's plan" : "dry run"}`}
    >
      {/* "will" when the ladder is live, "would" while it only watches. That
          one word is the difference between a report and a member being
          contacted, so it is never abbreviated or implied. */}
      <PanelHeadline
        failed={!report && !data}
        failedText="Could not load the Promise sweep. This is not a quiet night."
        empty={!!report && prompts.length === 0 && closes.length === 0}
        emptyText={
          live
            ? "At 6:00 PM tonight the tick has nobody to prompt and nobody to release."
            : "If the ladder were live tonight it would have nobody to prompt and nobody to release."
        }
      >
        At 6:00 PM tonight, <Fig>{prompts.length}</Fig>{" "}
        {prompts.length === 1 ? "pair" : "pairs"} {live ? "get" : "would get"} a
        nudge and <Fig>{closes.length}</Fig> {live ? "are" : "would be"} released.
        {!live && (
          <span className="ms-2 px-8 py-2 radius-4 text-xs fw-semibold bg-neutral-200 text-secondary-light">
            dry run, nobody is contacted
          </span>
        )}
      </PanelHeadline>

      <div className="d-flex align-items-center justify-content-between mb-3">
        <div>
          <span className="text-sm text-secondary-light">
            {live
              ? "What the 6:00 PM tick will do, per pair, from the ladder's own rules. This panel only looks; the tick acts."
              : "Who would be prompted or closed if the ladder were live. Read-only; nothing here contacts a member."}
          </span>
        </div>
        <div className="text-end">
          <span
            className={
              "badge me-2 " + (data?.dryRunFlag ? "bg-success" : "bg-secondary")
            }
          >
            Dry run {data?.dryRunFlag ? "ON" : "OFF"}
          </span>
          <span
            className={"badge " + (data?.liveFlag ? "bg-danger" : "bg-secondary")}
          >
            Live ladder {data?.liveFlag ? "ON" : "DORMANT"}
          </span>
        </div>
      </div>

      {failed && (
        <div className="alert alert-danger py-2">
          Could not load the Promise plan. Refresh to retry.
        </div>
      )}
      {!failed && !report && (
        <div className="alert alert-info py-2">No sweep available yet.</div>
      )}

      {summary && (
        <>
          <div className="row g-2 mb-3">
            {[
              { label: "Live pairs swept", value: summary.pairsSwept },
              { label: "Healthy", value: summary.healthy },
              { label: live ? "Prompts tonight" : "Would prompt", value: live ? prompts : summary.wouldPrompt },
              { label: live ? "Closes tonight" : "Would close", value: live ? closes : summary.wouldClose },
              { label: "Both quiet", value: summary.bothQuiet },
              { label: "Two-way stalled", value: summary.twoWayStalled },
            ].map((s) => (
              <div className="col" key={s.label}>
                <div className="border rounded text-center py-2 px-1 h-100">
                  <div className="fw-bold">{s.value ?? 0}</div>
                  <small className="text-muted">{s.label}</small>
                </div>
              </div>
            ))}
          </div>
          <div className="text-sm text-secondary-light mb-2">
            {report.reportDate === "live"
              ? `Live snapshot ${new Date(report.liveAt).toLocaleTimeString("en-CA", { hour: "numeric", minute: "2-digit" })} · next tick ${fmtWhen(nextTickIso, nextTickIso)}`
              : `Sweep of ${report.reportDate || "today"}`}
            {" · "}one clock for every pair: nudged after {summary.promptDays} day quiet, released
            after {summary.closeDays} (the nudge must be 24h old) · meetup pairs exempt: {summary.exemptMeetup ?? 0} · test pairs skipped:{" "}
            {summary.skippedTest ?? 0} · caps 50 prompts / 25 closes per tick
          </div>
        </>
      )}

      {actionable.length > 0 && (
        <div className="table-responsive">
          <table className="table bordered-table mb-0">
            <thead>
              <tr>
                <th>Tonight</th>
                <th>Shape</th>
                <th>Stage</th>
                <th>Quiet member</th>
                <th className="text-center">Days silent</th>
                <th>Presence</th>
                <th>Waiting member</th>
              </tr>
            </thead>
            <tbody>
              {actionable.map((c, i) => (
                <tr key={i}>
                  <td>
                    <span className={"badge " + planClass(c)}>
                      {planLabel(c, live, nextTickIso)}
                    </span>
                    {c.plan?.prompted && (
                      <div className="text-xs text-secondary-light mt-1">
                        prompted {new Date(c.promptedAt).toLocaleDateString("en-CA", { month: "short", day: "numeric" })}
                      </div>
                    )}
                  </td>
                  <td className="text-sm">{shapeLabel(c)}</td>
                  <td>{c.stage === "vault" ? "Vault" : "Chat"}</td>
                  <td>{c.silent}</td>
                  <td className="text-center">{c.silentDays}</td>
                  <td>
                    {c.presence === "activeGhost"
                      ? "In the app, silent here"
                      : "Away from the app"}
                  </td>
                  <td>{c.waiting}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {quiet.length > 0 && (
        <div className="mt-3">
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={() => setShowQuiet(!showQuiet)}
          >
            {showQuiet ? "Hide" : "Show"} the {quiet.length} pairs quiet on both
            sides
          </button>
          {showQuiet && (
            <div className="table-responsive mt-2">
              <table className="table bordered-table mb-0">
                <thead>
                  <tr>
                    <th>Plan</th>
                    <th>Stage</th>
                    <th>Members</th>
                    <th className="text-center">Days quiet</th>
                  </tr>
                </thead>
                <tbody>
                  {quiet.map((c, i) => (
                    <tr key={i}>
                      <td>
                        <span className={"badge " + planClass(c)}>
                          {planLabel(c, live, nextTickIso)}
                        </span>
                      </td>
                      <td>{c.stage === "vault" ? "Vault" : "Chat"}</td>
                      <td>
                        {c.members
                          ? c.members.join(" + ")
                          : `${c.silent} + ${c.waiting}`}
                      </td>
                      <td className="text-center">
                        {c.silentDays ?? c.daysSinceLastMessage}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </AnalyticsSection>
  );
};

export default CuriouzzPromiseStats;
